import React, {useLayoutEffect, useRef} from 'react';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';


import styled from 'styled-components';

import { useScroll } from '../../../context/scrolltocontext';
import { useResize } from '../../../context/resizecontext';

import PortfolioOptions from './PortfolioOptions';
import DevIcons from '../../components/DevIcons';
import Gauges from '../../components/gauge';

import markportrait from '../../assets/images/markportrait.svg'
import confetti from '../../assets/images/confetti.svg'
import aboutbg from '../../assets/images/aboutbg.svg';

gsap.registerPlugin(ScrollTrigger);


const About = styled.section`
    position: relative;
    box-sizing: border-box;
    width: 100%;
    background-image: url(${aboutbg});
    background-color: #FEDA00;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    background-attachment: scroll;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 15px;
    padding-bottom: 50px;
`;

const AboutContent = styled.div`
    box-sizing: border-box;
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
    max-width: 1500px;
    border: 0px solid #ffffff80;
    padding: 50px 50px;
    gap: 20px;

    @media (min-width: 1100px) {
        grid-template-columns: 3fr 1fr;
        width: 100%;
        align-items: center;
        justify-content: center;
        padding: 50px 50px 50px 100px;
    }
`;

const BioContainer = styled.aside`
    display: none;
    box-sizing: border-box;
    width: fit-content;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 20px;
    
    @media (min-width: 1100px) {
      display: flex;
    }
`;

const BioContainerInner = styled.aside`
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    border: 5px solid #ffffff90;
    border-radius: 20px;
    background-color: #f5f5f5DD;
    margin-top: 0px;
    padding: 10px 20px;
    width: 300px;
`;


const MarkPortrait = styled.img`
    @media (min-width: 1100px) {
      width: 400px;
      display: block;
    }
`;

const PortraitText = styled.div`
    display: flex;
    flex-direction: column;
    font-family: 'Roboto-Regular';
    color: #333333;
    font-size: 0.7rem;
    width: 100%;

    span {
        display: block;
        font-family: 'Bebas-Regular';
        color: #8000ff;
        font-size: 1.8rem;
    }

`;

const PortraitButton = styled.button`
    box-sizing: border-box;
    font-family: "Bebas-Regular";
    background: #fff;
    border: 2px solid #6c35ff;
    background-color:  #8000ff;
        color: #fff;
    font-size: 1.15rem;
    text-transform: uppercase;
    padding: 5px;
    border-radius: 10px;
    cursor: pointer;
    outline: 0;
    margin-top: 10px;
    transition: background-color 120ms ease-out;


    &:hover {
        background: #fff;
        border: 2px solid #ffffff80;
        color: #8000ff;
        outline: 0;
    }
`;

const AboutText = styled.section`
    box-sizing: border-box;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 50px;

    h2 {
        font-family: "Oswald";
        font-size: 3.5rem;
        color:  #8000ff;
    }

    p:nth-of-type(1){
        font-family: "Oswald";
        font-size: 1.75rem;
        color: #333333;
        line-height: 170%;
        border-left: 5px solid black;
        padding-left: 15px;

        span {
            background-color: #fff;
        }   
    }

    p:not(:nth-of-type(1)){
        font-family: "Roboto-Regular";
        font-size: 1.15rem;
        color: #361A52;
        line-height: 155%;
        width: 95%;
    }

    p:last-of-type{
        box-sizing: border;
        font-family: "Roboto-Regular";
        font-size: 0.9rem;
        color: #333;
        width: 80%;
        padding: 20px 60px;
        margin-top: 20px;    
        border: 5px solid #ffffff90;
        border-radius: 20px;
        background-color: #f5f5f5;
        position: relative;
        line-height: 180%;
    }

    p:last-of-type span {
    font-family: "Bebas-Regular";
        color:  #8000ff;
        display: block;
        font-size: 240%;
        text-transform: uppercase;
        margin-bottom: 10px;
    }

    p:last-of-type::after {
     content: '';
    position: absolute;
    top: -20%;
    left: -35px;
    width: 85px;
    height: 85px;
    background-image: url(${confetti});
    background-size: cover; /* Adjust how the image scales */
    background-position: center; /* Adjust image position */
    background-repeat: no-repeat; /* Prevent tiling */
    }

`;



interface LineProps {
    value: number;
}

const StraightLine: React.FC<LineProps> = ({ value }) => {
    const lineRef = useRef<SVGLineElement>(null);

    const {width, height} = useResize();

    useLayoutEffect(() => {
        let timeout = setTimeout(() => {
            if (!lineRef.current) return;

            const bbox = lineRef.current.getBBox();
            if (bbox.width === 0 && bbox.height === 0) return;

            const totalLength = lineRef.current.getTotalLength();

            const anim = gsap.fromTo(
                lineRef.current,
                { strokeDasharray: totalLength, strokeDashoffset: totalLength },
                {
                    strokeDashoffset: 0,
                    duration: 2,
                    ease: 'power1.out',
                    scrollTrigger: {
                        trigger: lineRef.current,
                        start: 'top 90%',
                        toggleActions: 'play none none none',
                    },
                }
            );

            // Optional: force a refresh after animation is set up
            ScrollTrigger.refresh();

            // Cleanup
            return () => {
                anim.scrollTrigger?.kill();
                anim.kill();
            };
        }, 1000);

        return () => clearTimeout(timeout);
    }, [value, width, height]);

    return (
        <svg
            width="250"
            height="8"
            viewBox="0 0 250 8"
            xmlns="http://www.w3.org/2000/svg"
        >
            <defs>
                <linearGradient id="line-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#7501FF" />
                    <stop offset="100%" stopColor="#0be00f" />
                </linearGradient>
            </defs>
            <line
                x1="0"
                y1="3"
                x2="245"
                y2="3"
                stroke="#33333320"
                strokeWidth="2"
            />
            <line
                ref={lineRef}
                x1="0"
                y1="3"
                x2={value}
                y2="3"
                stroke="#0be00f"
                strokeWidth="8"
                strokeLinecap="round"
            />
        </svg>
    );
};

const HomeAbout: React.FC = () => {

    const { contactRef, aboutRef, scrollToSection } = useScroll();

return (
<About ref={aboutRef} id="about">
    <PortfolioOptions />
    <DevIcons />
    <AboutContent>
        <AboutText>
            <h2>HI, I'M MARK!</h2>
            <p>I'M A FULL-TIME GRAPHIC DESIGNER AND WEB DEVELOPER DRIVEN BY A PASSION FOR CREATING INTERACTIVE AND ENGAGING EXPERIENCES.</p>
            <p>I specialize in combining typography and imagery to create impactful content for marketing campaigns and other business projects. Print or Web I have many years of experience related to creating everything your business needs for successful marketing. <br /> <br />
            Living in Barrie, Ontario my interests revolve around computers, graphic design, web development, and marketing. Sorry, I'm not into video games or Pokemon! I'm focused on getting work done, helping businesses grow, and growing my experience and career. </p>
            <p><span>I graduated Georgian College!</span>Due to covid's affect on the economy I decided to take time away from full-time employment to pursue Georgian College's Frontend Web Development program. There I was able to focus on learning the fundamentals and current standards of web development. I successfully graduated on the Dean's List in 2023. </p>
        </AboutText>
        <BioContainer>
        <MarkPortrait src={markportrait} />
            <BioContainerInner>
                <PortraitText>
                    <div><span>Mark McGuigan</span></div>
                    <div>WEB & PRINT DESIGNER</div>
                    <div><StraightLine value={200}/></div>
                    <div>WEB DEVELOPER</div>
                    <div><StraightLine value={150}/></div>
                    <PortraitButton onClick={() => scrollToSection(contactRef)}>CONTACT ME</PortraitButton>
                </PortraitText>
            </BioContainerInner>
        </BioContainer>
    </AboutContent>
    <Gauges />
</About>      
);

};

export default HomeAbout;